/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Box, Text, Button } from '@indeed/ifl-components';
import { ChevronUp, ChevronDown, Settings, Refresh, TrashCan } from '@indeed/ifl-icons';
import JSONTree from './JSONTree';

interface LocalStorageInspectorProps {
    position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
}

/**
 * LocalStorage Inspector Component
 *
 * Provides real-time localStorage monitoring for development environments using local mocks.
 * Only renders when localStorage database (mock system) is available.
 * Similar to ApolloCacheInspector but for localStorage data.
 *
 * Features:
 * - Real-time localStorage monitoring with deep change detection
 * - Interactive localStorage data tree with expand/collapse functionality
 * - Smart focused expansion: highlights and expands only updated nested paths
 * - Deep path highlighting (e.g., "graphql-db:metadata.lastUpdated" not just "graphql-db:metadata")
 * - Persistent highlights until next localStorage update
 * - Manual expand/collapse with user interaction tracking
 * - Status indicators for localStorage state and usage
 * - Only appears when using local mocks (window.graphqlDB available)
 */
const LocalStorageInspector: React.FC<LocalStorageInspectorProps> = ({
    position = 'bottom-right',
}) => {
    // Client-side mounting state
    const [isMounted, setIsMounted] = useState(false);

    // All hooks must be called unconditionally at the top
    const [isExpanded, setIsExpanded] = useState(true);
    const [isVisible, setIsVisible] = useState(true);
    const [portalContainer, setPortalContainer] = useState<Element | null>(null);
    const [highlightedPaths, setHighlightedPaths] = useState<Set<string>>(new Set());
    const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());

    // Client-side only mounting - prevents SSR issues
    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Check if local mocks are being used
    const isUsingLocalMocks = useMemo(() => {
        if (!isMounted || typeof window === 'undefined') return false;

        // Check if localStorage database (mock system) is available
        return typeof (window as any).graphqlDB !== 'undefined';
    }, [isMounted]);

    // SSR-safe portal container setup
    useEffect(() => {
        if (isMounted && typeof window !== 'undefined') {
            const container = document.getElementById('development-info-portal') || document.body;
            setPortalContainer(container);
        }
    }, [isMounted]);

    // LocalStorage data change detection
    const previousLocalStorageRef = useRef<Record<string, string>>({});

    // Get localStorage content as structured data
    const getLocalStorageContent = useMemo(() => {
        if (
            process.env.NODE_ENV !== 'development' ||
            !isMounted ||
            !isUsingLocalMocks ||
            typeof window === 'undefined'
        )
            return {};

        try {
            const localStorage = window.localStorage;
            const content: Record<string, any> = {};

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key) {
                    const value = localStorage.getItem(key);
                    if (value) {
                        try {
                            // Try to parse as JSON for better visualization
                            content[key] = JSON.parse(value);
                        } catch {
                            // If not JSON, store as string
                            content[key] = value;
                        }
                    }
                }
            }

            return content;
        } catch {
            return { error: 'Failed to read localStorage' };
        }
    }, [isMounted, isUsingLocalMocks, lastUpdateTime]); // React to mounting, local mocks, and manual refresh

    // Monitor localStorage changes
    useEffect(() => {
        if (
            process.env.NODE_ENV !== 'development' ||
            !isMounted ||
            !isUsingLocalMocks ||
            typeof window === 'undefined'
        )
            return;

        const checkLocalStorageChanges = () => {
            const currentData = window.localStorage;
            const currentSnapshot: Record<string, string> = {};

            // Create snapshot of current localStorage
            for (let i = 0; i < currentData.length; i++) {
                const key = currentData.key(i);
                if (key) {
                    const value = currentData.getItem(key);
                    if (value !== null) {
                        currentSnapshot[key] = value;
                    }
                }
            }

            const previousData = previousLocalStorageRef.current;

            // Compare with previous snapshot
            if (Object.keys(previousData).length > 0) {
                const currentSerialized = JSON.stringify(currentSnapshot);
                const previousSerialized = JSON.stringify(previousData);

                if (currentSerialized !== previousSerialized) {
                    // Find detailed changed paths (not just top-level keys)
                    const changedPaths = findDetailedChangedPaths(previousData, currentSnapshot);

                    if (changedPaths.size > 0) {
                        setHighlightedPaths(changedPaths);
                        setLastUpdateTime(Date.now());
                    }
                }
            }

            // Update the previous data reference
            previousLocalStorageRef.current = currentSnapshot;
        };

        // Initial check
        checkLocalStorageChanges();

        // Set up periodic checking (since localStorage doesn't have native change events)
        const intervalId = setInterval(checkLocalStorageChanges, 1000);

        // Listen for storage events from other tabs
        const handleStorageEvent = (e: StorageEvent) => {
            if (e.storageArea === localStorage) {
                checkLocalStorageChanges();
            }
        };

        window.addEventListener('storage', handleStorageEvent);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', handleStorageEvent);
        };
    }, [isMounted, isUsingLocalMocks]);

    // Utility function to find detailed changed paths in localStorage
    const findDetailedChangedPaths = (
        oldData: Record<string, string>,
        newData: Record<string, string>,
    ): Set<string> => {
        const changedPaths = new Set<string>();

        // Get all keys from both old and new data
        const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

        allKeys.forEach((key) => {
            const oldValue = oldData[key];
            const newValue = newData[key];

            // Handle deleted keys
            if (oldValue !== undefined && newValue === undefined) {
                changedPaths.add(key);
                return;
            }

            // Handle new keys
            if (oldValue === undefined && newValue !== undefined) {
                changedPaths.add(key);
                return;
            }

            // Handle modified keys
            if (oldValue !== newValue) {
                // Try to parse both values as JSON for deep comparison
                let oldParsed: any = oldValue;
                let newParsed: any = newValue;

                try {
                    oldParsed = JSON.parse(oldValue);
                } catch {
                    // Not JSON, keep as string
                }

                try {
                    newParsed = JSON.parse(newValue);
                } catch {
                    // Not JSON, keep as string
                }

                // If both are objects, do deep comparison
                if (
                    typeof oldParsed === 'object' &&
                    typeof newParsed === 'object' &&
                    oldParsed !== null &&
                    newParsed !== null
                ) {
                    const nestedChanges = findChangedPaths(oldParsed, newParsed, key);
                    nestedChanges.forEach((path) => changedPaths.add(path));
                } else {
                    // For primitive values or non-JSON strings, highlight the whole key
                    changedPaths.add(key);
                }
            }
        });

        return changedPaths;
    };

    // Utility function to find changed paths in nested objects (similar to ApolloCacheInspector)
    const findChangedPaths = (
        oldObj: any,
        newObj: any,
        basePath = '',
        changedPaths: Set<string> = new Set(),
    ): Set<string> => {
        if (oldObj === newObj) return changedPaths;

        // Handle null/undefined cases
        if ((oldObj == null) !== (newObj == null)) {
            if (basePath) {
                changedPaths.add(basePath);
            }
            return changedPaths;
        }

        // Handle primitive values
        if (
            typeof oldObj !== 'object' ||
            typeof newObj !== 'object' ||
            oldObj == null ||
            newObj == null
        ) {
            if (oldObj !== newObj && basePath) {
                changedPaths.add(basePath);
            }
            return changedPaths;
        }

        // Handle arrays
        if (Array.isArray(oldObj) && Array.isArray(newObj)) {
            const maxLength = Math.max(oldObj.length, newObj.length);
            for (let i = 0; i < maxLength; i++) {
                const itemPath = basePath ? `${basePath}.[${i}]` : `[${i}]`;
                findChangedPaths(oldObj[i], newObj[i], itemPath, changedPaths);
            }
            return changedPaths;
        }

        // Handle objects - recursively check properties
        const allKeys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);
        for (const key of allKeys) {
            const keyPath = basePath ? `${basePath}.${key}` : key;
            findChangedPaths(oldObj[key], newObj[key], keyPath, changedPaths);
        }

        return changedPaths;
    };

    // Generate status indicators based on localStorage state
    const generateStatusIndicators = () => {
        const indicators = [];
        const storageContent = getLocalStorageContent;

        // LocalStorage Status
        indicators.push({
            label: 'LocalStorage',
            value:
                isMounted && typeof window !== 'undefined' && window.localStorage
                    ? 'Available'
                    : 'Unavailable',
            color:
                isMounted && typeof window !== 'undefined' && window.localStorage
                    ? 'success'
                    : 'error',
        });

        // Item Count
        const itemCount = Object.keys(storageContent).length;
        indicators.push({
            label: 'Items',
            value: itemCount,
            color: itemCount > 0 ? 'success' : 'warning',
        });

        // Storage Usage (approximate)
        if (isMounted && typeof window !== 'undefined' && window.localStorage) {
            try {
                const totalSize = JSON.stringify(storageContent).length;
                const sizeKB = Math.round(totalSize / 1024);
                indicators.push({
                    label: 'Size',
                    value: sizeKB > 0 ? `~${sizeKB}KB` : '<1KB',
                    color: sizeKB > 1000 ? 'warning' : 'success',
                });
            } catch {
                indicators.push({
                    label: 'Size',
                    value: 'Unknown',
                    color: 'info',
                });
            }
        }

        // Change Detection
        if (highlightedPaths.size > 0) {
            indicators.push({
                label: 'Changes',
                value: highlightedPaths.size,
                color: 'warning',
            });
        }

        return indicators;
    };

    // Action handlers
    const handleRefresh = () => {
        if (!isMounted) return;
        setLastUpdateTime(Date.now());
        setHighlightedPaths(new Set()); // Clear highlights on manual refresh
    };

    const handleClearAll = () => {
        if (!isMounted || typeof window === 'undefined' || !window.localStorage) return;

        if (confirm('Clear all localStorage data? This cannot be undone.')) {
            window.localStorage.clear();
            setLastUpdateTime(Date.now());
            setHighlightedPaths(new Set());
        }
    };

    const handleClearHighlights = () => {
        setHighlightedPaths(new Set());
    };

    // Conditional rendering checks AFTER all hooks
    if (process.env.NODE_ENV !== 'development') {
        return null;
    }

    // Don't render until mounted on client-side (prevents SSR issues)
    if (!isMounted) {
        return null;
    }

    // Only render when using local mocks (localStorage database available)
    if (!isUsingLocalMocks) {
        return null;
    }

    // Don't render until we have a portal container (client-side only)
    if (!portalContainer) {
        return null;
    }

    const statusIndicators = generateStatusIndicators();

    // Position styles (same as ApolloCacheInspector)
    const getPositionStyles = () => {
        const base = {
            position: 'fixed' as const,
            zIndex: 9998, // Slightly lower than ApolloCacheInspector
            transition: 'all 0.3s ease',
        };

        switch (position) {
            case 'bottom-right':
                return { ...base, bottom: '20px', right: '20px' };
            case 'bottom-left':
                return { ...base, bottom: '20px', left: '20px' };
            case 'top-right':
                return { ...base, top: '20px', right: '20px' };
            case 'top-left':
                return { ...base, top: '20px', left: '20px' };
            default:
                return { ...base, bottom: '20px', right: '20px' };
        }
    };

    // Status color function (same as ApolloCacheInspector)
    const getStatusColor = (color: string, value: boolean | string | number) => {
        const colorMap = {
            success: { bg: '#d1e7dd', color: '#0f5132' },
            warning: { bg: '#fff3cd', color: '#664d03' },
            error: { bg: '#f8d7da', color: '#721c24' },
            info: { bg: '#cff4fc', color: '#055160' },
        };

        if (color) {
            return colorMap[color as keyof typeof colorMap] || colorMap.info;
        }

        // Auto-detect based on value
        if (typeof value === 'boolean') {
            return value
                ? { bg: '#d1e7dd', color: '#0f5132' }
                : { bg: '#f8d7da', color: '#721c24' };
        }

        if (typeof value === 'string') {
            if (
                value.toLowerCase().includes('error') ||
                value.toLowerCase().includes('unavailable')
            ) {
                return { bg: '#f8d7da', color: '#721c24' };
            }
            if (
                value.toLowerCase().includes('loading') ||
                value.toLowerCase().includes('checking')
            ) {
                return { bg: '#fff3cd', color: '#664d03' };
            }
            if (
                value.toLowerCase().includes('available') ||
                value.toLowerCase().includes('ready')
            ) {
                return { bg: '#d1e7dd', color: '#0f5132' };
            }
        }

        return { bg: '#f8f9fa', color: '#6c757d' };
    };

    // Format value function (same as ApolloCacheInspector)
    const formatValue = (value: any): string => {
        if (typeof value === 'boolean') return value ? '✓' : '✗';
        if (typeof value === 'object' && value !== null) return JSON.stringify(value, null, 2);
        return String(value);
    };

    if (!isVisible) {
        return null;
    }

    const devPanel = (
        <Box
            sx={{
                ...getPositionStyles(),
                maxInlineSize: isExpanded ? '600px' : '320px',
                backgroundColor: 'rgba(248, 249, 250, 0.95)',
                backdropFilter: 'blur(8px)',
                border: '1px solid #e4e2e0',
                borderRadius: 'md',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                fontFamily: 'monospace',
                fontSize: '14px',
                overflow: 'hidden',
                transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
                opacity: isVisible ? 1 : 0,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: '#17a2b8', // Teal color to differentiate from Apollo (which uses grey)
                    color: 'white',
                    cursor: 'pointer',
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Settings size="sm" />
                    <Text sx={{ fontSize: '13px', fontWeight: 'bold', color: 'white' }}>
                        localStorage Inspector
                    </Text>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsVisible(!isVisible);
                        }}
                        sx={{
                            minInlineSize: 'auto',
                            padding: '2px',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        }}
                    >
                        ×
                    </Button>
                    {isExpanded ? <ChevronDown size="sm" /> : <ChevronUp size="sm" />}
                </Box>
            </Box>

            {/* Status Indicators */}
            {statusIndicators.length > 0 && (
                <Box sx={{ padding: '8px 12px', borderBlockEnd: '1px solid #e4e2e0' }}>
                    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {statusIndicators.map((indicator, index) => {
                            const colors = getStatusColor(indicator.color, indicator.value);
                            return (
                                <Text
                                    key={index}
                                    sx={{
                                        fontSize: '12px',
                                        backgroundColor: colors.bg,
                                        color: colors.color,
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        border: `1px solid ${colors.color}20`,
                                        fontWeight: '500',
                                    }}
                                >
                                    {indicator.label}: {formatValue(indicator.value)}
                                </Text>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* LocalStorage Content (Expanded) */}
            {isExpanded && (
                <Box
                    sx={{
                        backgroundColor: '#f8f9fa',
                        maxBlockSize: '500px',
                        overflowY: 'auto',
                    }}
                >
                    {/* LocalStorage Section */}
                    <Box
                        sx={{
                            padding: '8px 12px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBlockEnd: '6px',
                            }}
                        >
                            <Text
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    color: '#495057',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}
                            >
                                LocalStorage ({Object.keys(getLocalStorageContent).length} items):
                                {highlightedPaths.size > 0 && (
                                    <Text
                                        sx={{
                                            fontSize: '10px',
                                            backgroundColor: '#ff9800',
                                            color: '#fff',
                                            padding: '2px 6px',
                                            borderRadius: 'md',
                                            fontWeight: 'bold',
                                            animation: 'blink 1s ease-in-out infinite',
                                            '@keyframes blink': {
                                                '0%, 50%': { opacity: 1 },
                                                '25%, 75%': { opacity: 0.7 },
                                            },
                                        }}
                                    >
                                        {highlightedPaths.size} UPDATED
                                    </Text>
                                )}
                            </Text>
                            <Box sx={{ display: 'flex', gap: '4px' }}>
                                <Button
                                    variant="tertiary"
                                    size="sm"
                                    onClick={handleRefresh}
                                    sx={{
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        '&:hover': { backgroundColor: '#bbdefb' },
                                    }}
                                >
                                    <Refresh size="sm" style={{ marginRight: '2px' }} />
                                    Refresh
                                </Button>
                                {highlightedPaths.size > 0 && (
                                    <Button
                                        variant="tertiary"
                                        size="sm"
                                        onClick={handleClearHighlights}
                                        sx={{
                                            fontSize: '10px',
                                            padding: '2px 6px',
                                            backgroundColor: '#fff3e0',
                                            color: '#f57c00',
                                            '&:hover': { backgroundColor: '#ffe0b2' },
                                        }}
                                    >
                                        Clear Highlights
                                    </Button>
                                )}
                                <Button
                                    variant="tertiary"
                                    size="sm"
                                    onClick={handleClearAll}
                                    sx={{
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                        backgroundColor: '#ffebee',
                                        color: '#c62828',
                                        '&:hover': { backgroundColor: '#ffcdd2' },
                                    }}
                                >
                                    <TrashCan size="sm" style={{ marginRight: '2px' }} />
                                    Clear All
                                </Button>
                            </Box>
                        </Box>

                        {Object.keys(getLocalStorageContent).length > 0 ? (
                            <JSONTree
                                data={getLocalStorageContent}
                                highlightedPaths={highlightedPaths}
                                maxHeight="400px"
                            />
                        ) : (
                            <Box
                                sx={{
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e4e2e0',
                                    borderRadius: '4px',
                                    padding: '20px',
                                    textAlign: 'center',
                                }}
                            >
                                <Text sx={{ fontSize: '10px', color: '#6c757d' }}>
                                    No localStorage items found
                                </Text>
                            </Box>
                        )}

                        <Box
                            sx={{
                                marginBlockStart: '8px',
                                padding: '4px',
                                backgroundColor: '#cff4fc',
                                borderRadius: '4px',
                            }}
                        >
                            <Text sx={{ fontSize: '10px', color: '#055160' }}>
                                💡 <strong>Tip:</strong> LocalStorage updates automatically when
                                data changes
                            </Text>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Minimized State */}
            {!isExpanded && statusIndicators.length === 0 && (
                <Box sx={{ padding: '8px 12px', textAlign: 'center' }}>
                    <Text sx={{ fontSize: '12px', color: '#6c757d' }}>
                        Click to expand localStorage info
                    </Text>
                </Box>
            )}
        </Box>
    );

    // Use React Portal to render outside the component tree
    return ReactDOM.createPortal(devPanel, portalContainer);
};

export default LocalStorageInspector;
